import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Paper, Typography } from '@mui/material';
import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { DeepsightComponentLoader } from '@commons/DeepsightComponents';
import { getClientInfo } from '@selectors/client';

import { COLORS, METRIC_TYPE_OPTIONS } from './utils/config';
import { METRIC_TYPE } from '../common/utils';
import ChartTooltip from '../common/ChartTooltip';
import RestaurantLimitedDropdown from '../common/RestaurantLimitedDropdown';

const ChannelComparisonChart = ({
  selectedStores,
  tableData,
  isLoading,
  isPercentage,
  client: { storeName },
  selectedMetric,
}) => {
  const [selectedStoresForComparison, setSelectedStoresForComparison] = useState([]);
  const [chartData, setChartData] = useState([]);

  // Prepare chart data
  useEffect(() => {
    if (!tableData || !selectedStoresForComparison.length) {
      return;
    }

    const data = selectedStoresForComparison.map((store) => {
      const storeData = tableData.find((data) => data.id === store.id) || {};

      if (isPercentage)
        return {
          name: store.name,
          eatin: { current: storeData.eatinPercentage?.current },
          takeaway: { current: storeData.takeawayPercentage?.current },
          delivery: { current: storeData.deliveryPercentage?.current },
        };

      return {
        name: store.name,
        eatin: { current: storeData.eatin?.current },
        takeaway: { current: storeData.takeaway?.current },
        delivery: { current: storeData.delivery?.current },
      };
    });

    setChartData(data);
  }, [tableData, selectedStoresForComparison, isPercentage]);

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, height: 500, border: '1px solid #E0E0E0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
          <DeepsightComponentLoader />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, height: 500, border: '1px solid #E0E0E0', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {i18next.t('ANALYSIS.PERFORMANCE.STORE_COMPARISON', { storeName })}
        </Typography>

        <RestaurantLimitedDropdown
          selectedStores={selectedStoresForComparison}
          setSelectedStores={setSelectedStoresForComparison}
          stores={selectedStores}
        />
      </Box>

      {chartData.length > 0 ? (
        <ResponsiveContainer height={380} width="100%">
          <BarChart data={chartData} margin={{ bottom: 5, left: 20, right: 30, top: 20 }}>
            <CartesianGrid stroke="#EDF0F6" strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fill: '#6B768C', fontSize: 12 }} tickLine={false} />
            <YAxis
              axisLine={false}
              tick={{ fill: '#6B768C', fontSize: 12 }}
              tickFormatter={(value) => (isPercentage ? `${value.toFixed(0)}%` : value)}
              tickLine={false}
            />
            <Tooltip
              content={
                <ChartTooltip
                  isPercentage={isPercentage}
                  options={METRIC_TYPE_OPTIONS().map((option) => ({
                    id: option.id,
                    type: isPercentage ? METRIC_TYPE.PERCENTAGE : option.type,
                  }))}
                  selectedMetric={selectedMetric}
                  displayTotal
                />
              }
              cursor={{ fill: 'rgba(0, 0, 0, 0.03)' }}
            />
            <Legend />

            {/* Dine In bars */}
            <Bar
              dataKey="eatin.current"
              fill={COLORS.eatin}
              name={i18next.t('ANALYSIS.PERFORMANCE.DINE_IN')}
              radius={[2, 2, 0, 0]}
              stackId="a"
            />

            {/* Takeaway bars */}
            <Bar
              dataKey="takeaway.current"
              fill={COLORS.takeaway}
              name={i18next.t('ANALYSIS.PERFORMANCE.TAKEAWAY')}
              radius={[2, 2, 0, 0]}
              stackId="a"
            />

            {/* Delivery bars */}
            <Bar
              dataKey="delivery.current"
              fill={COLORS.delivery}
              name={i18next.t('ANALYSIS.PERFORMANCE.DELIVERY')}
              radius={[2, 2, 0, 0]}
              stackId="a"
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 380 }}>
          <Typography color="textSecondary" variant="body2">
            {i18next.t('GENERAL.NO_DATA_TO_DISPLAY')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
});

export default connect(mapStateToProps, null)(ChannelComparisonChart);
